import CampaignIcon from '@mui/icons-material/Campaign'
import { Box, Link, Paper, Stack, Tab, Tabs, useTheme } from '@mui/material'
import { AxiosError } from 'axios'
import { useFormik } from 'formik'
import { nanoid } from 'nanoid'
import { Suspense, useCallback, useEffect, useState } from 'react'
import {
  Outlet,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'

import EditableTitle from '~/components/editableTitle'
import LoadingPaperSkeleton from '~/components/loadingPaperSkeleton'
import NotFound from '~/components/NotFound'
import useCreateCampaign from '~/features/campaigns/hooks/useCreateCampaign'
import useGetCampaignById from '~/features/campaigns/hooks/useGetCampaignById'
import useUpdateCampaign from '~/features/campaigns/hooks/useUpdateCampaign'
import {
  ECampaignChannel,
  ECampaignExecutionStatus,
  ECampaignKeys,
  ECampaignStatus,
  ECampaignTabs,
  TCampaign,
  TCampaignCreatePayload,
  TCampaignError,
  TCampaignFact,
  TCampaignFormFields,
  TCampaignUpdatePayload,
} from '~/features/campaigns/models'
import {
  campaignValidationSchema,
  extractFriendlyErrorMessage,
  getArrayFromStringList,
  getMergeFields,
  getStringListFromArray,
  getTabFromRoute,
  showToastOnCampaignErrorResponse,
} from '~/features/campaigns/utils'
import {
  CREATE_CAMPAIGN_DEFAULT_ID,
  DEFAULT_CAMPAIGN_NAME,
  DEFAULT_CAMPAIGN_QUESTION,
} from '~/features/campaigns/utils/constants'
import { TKnowledgeTableDataItem } from '~/features/knowledge/v1/models'
import { getChangedValues } from '~/features/settings/utils'
import { EClientPath } from '~/models/api'
import { sessionStore } from '~/store'
import pathBuilder from '~/utils/api'
import rem from '~/utils/ui'

function CreateEditCampaignContainer() {
  const theme = useTheme()
  const location = useLocation()
  const { id } = useParams()
  const [urlSearchParams] = useSearchParams()
  const isCopy = urlSearchParams.get('isCopy') === 'true'
  const navigate = useNavigate()
  const [tab, setTab] = useState(
    getTabFromRoute(location.pathname, id || CREATE_CAMPAIGN_DEFAULT_ID),
  )
  /*
  SaveAndTestDialog is a child component of CreateEditCampaignContainer
  I moved the open / close state here because of the current logic in place:
  * navigating once the campaign is created
  * rendering different components ( Loading ) if the campaign is creating, hence modifying the tree structure 
  */
  const [showSaveAndTestDialog, setShowSaveAndTestDialog] =
    useState<boolean>(false)

  const agentId = sessionStore((state) => state.agent.id) || ''

  const {
    data: { data } = {},
    isLoading,
    isError,
    error,
  } = useGetCampaignById((id !== CREATE_CAMPAIGN_DEFAULT_ID ? id : '') || '')

  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign()
  const { mutate: updateCampaign } = useUpdateCampaign(data?.id || '')

  const handleSubmitCreate = (
    values: TCampaignFormFields,
    questions: string[] | undefined,
    campaignFacts: TCampaignFact[] | undefined,
    rules: string[] | undefined,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { id, ...valuesWithoutId } = values

    const createCampaignValues: TCampaignCreatePayload = {
      ...valuesWithoutId,
      questions,
      campaignFacts,
      rules,
      agentId,
    }

    createCampaign(createCampaignValues, {
      onSuccess: (response) => {
        if ((response as TCampaignError).error) {
          toast.error(
            `Failed to create campaign. ${extractFriendlyErrorMessage(
              response as TCampaignError,
            )}`,
          )
          return
        }

        toast.success('Campaign created successfully')
        navigate({
          pathname: pathBuilder(EClientPath.campaignCreateEdit, {
            id: (response as TCampaign)?.id,
          }),
        })
      },
      onError: (response) =>
        showToastOnCampaignErrorResponse(response, 'Failed to create campaign'),
    })
  }

  const handleSubmitUpdate = (
    values: TCampaignFormFields,
    questions: string[] | undefined,
    campaignFacts: TCampaignFact[] | undefined,
    rules: string[] | undefined,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const payload = getChangedValues(values, formik.initialValues)

    const updateCampaignValues = {
      ...payload,
      questions,
      campaignFacts,
      rules,
    } as TCampaignUpdatePayload

    updateCampaign(updateCampaignValues, {
      onSuccess: () => {
        toast.success('Campaign updated successfully')
      },
      onError: (response) =>
        showToastOnCampaignErrorResponse(response, 'Failed to update campaign'),
    })
  }

  const formik = useFormik<TCampaignFormFields>({
    initialValues: {
      [ECampaignKeys.id]: isCopy ? '' : data?.id || '',
      [ECampaignKeys.campaignFacts]: !data?.campaignFacts
        ? []
        : data?.campaignFacts.map((fact) => {
            return {
              id: fact.id || nanoid(),
              fact: fact.text,
            } as TKnowledgeTableDataItem
          }),
      [ECampaignKeys.campaignName]: data?.campaignName || DEFAULT_CAMPAIGN_NAME,
      [ECampaignKeys.campaignObjective]: data?.campaignObjective || '',
      [ECampaignKeys.channel]: data?.channel || ECampaignChannel.phone,
      [ECampaignKeys.introMessage]: data?.introMessage || '',
      [ECampaignKeys.mergeFields]: getMergeFields(
        data?.mergeFields || [],
        data?.channel || ECampaignChannel.phone,
      ),
      [ECampaignKeys.questions]: getStringListFromArray(
        data?.questions || [DEFAULT_CAMPAIGN_QUESTION],
      ),
      [ECampaignKeys.rules]: getStringListFromArray(data?.rules),
      [ECampaignKeys.status]: isCopy
        ? ECampaignStatus.draft
        : data?.status || ECampaignStatus.draft,
      [ECampaignKeys.useAgentFacts]: data?.useAgentFacts ?? true,
      [ECampaignKeys.voicemailMessage]: data?.voicemailMessage || '',
      [ECampaignKeys.executionStatus]:
        data?.executionStatus || ECampaignExecutionStatus.notStarted,
      [ECampaignKeys.startDate]: data?.startDate || undefined,
    },
    enableReinitialize: true,
    validationSchema: campaignValidationSchema,
    onSubmit: (values) => {
      if (!formik.dirty && !isCopy) {
        return
      }

      const questions = getArrayFromStringList(values[ECampaignKeys.questions])
      const campaignFacts = values[ECampaignKeys.campaignFacts]?.map((fact) => {
        return {
          id: fact.id,
          text: fact?.fact,
        } as TCampaignFact
      })
      const rules = getArrayFromStringList(values[ECampaignKeys.rules])

      if (isCopy || id === CREATE_CAMPAIGN_DEFAULT_ID) {
        handleSubmitCreate(values, questions, campaignFacts, rules)
      } else {
        handleSubmitUpdate(values, questions, campaignFacts, rules)
      }
    },
  })

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent | null, newTab: ECampaignTabs) => {
      let pathByTab

      switch (newTab) {
        case ECampaignTabs.contacts:
          pathByTab = pathBuilder(EClientPath.campaignContacts, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
        case ECampaignTabs.results:
          pathByTab = pathBuilder(EClientPath.campaignResults, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
        case ECampaignTabs.details:
        default:
          pathByTab = pathBuilder(EClientPath.campaignDetails, {
            id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
          })
          break
      }

      setTab(newTab)
      navigate(pathByTab)
    },
    [id, navigate, isCopy],
  )

  useEffect(() => {
    if (
      location.pathname ===
      pathBuilder(EClientPath.campaignCreateEdit, {
        id: isCopy || !id ? CREATE_CAMPAIGN_DEFAULT_ID : id,
      })
    ) {
      handleTabChange(null, ECampaignTabs.details)
    }
  }, [handleTabChange, id, location.pathname, navigate, isCopy])

  const shouldDisplayNotFound =
    isError &&
    (error as AxiosError)?.response?.status === 404 &&
    (error as AxiosError<{ message: string }>)?.response?.data?.message ===
      'Campaign not found'

  if (isError && !shouldDisplayNotFound) {
    toast.error('Error loading campaign')
    navigate(EClientPath.campaignList)

    return null
  }

  return (
    <>
      <Link
        to={EClientPath.campaignList}
        component={RouterLink}
        sx={{
          width: 'fit-content',
        }}
      >
        View all campaigns
      </Link>
      {isError && shouldDisplayNotFound && (
        <NotFound message="No campaign with that Id exists on this assistant." />
      )}
      {!isError && (
        <form onSubmit={formik.handleSubmit}>
          <Stack
            spacing={2}
            direction="row"
            sx={{ alignItems: 'center' }}
            marginBottom={2}
          >
            <CampaignIcon fontSize="large" />
            {isLoading ? (
              <LoadingPaperSkeleton sx={{ height: '2.9rem' }} />
            ) : (
              <EditableTitle
                name={ECampaignKeys.campaignName}
                ariaLabel="Campaign name"
                value={formik.values.campaignName}
                onChange={formik.handleChange}
                error={
                  formik.touched.campaignName &&
                  Boolean(formik.errors.campaignName)
                }
                helperText={
                  formik.touched.campaignName ? formik.errors.campaignName : ''
                }
                onSubmit={() =>
                  !isCopy &&
                  id !== CREATE_CAMPAIGN_DEFAULT_ID &&
                  updateCampaign({ campaignName: formik.values.campaignName })
                }
              />
            )}
          </Stack>
          <Box sx={{ width: '100%' }}>
            <Box>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                aria-label="campaign tabs"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '18px',
                    color: theme.palette.secondaryButtonFont,
                    width: { xxs: '114px', xs: rem(114), md: rem(170) },
                    height: rem(60),
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'transparent',
                  },

                  '& .MuiButtonBase-root.MuiTab-root.Mui-selected': {
                    color: theme.palette.secondaryButtonFont,
                    background: theme.palette.common.white,
                  },
                }}
              >
                <Tab label="Configure" />
                <Tab label="Contacts" disabled={!data?.id} />
                <Tab label="Results" disabled={!data?.id} />
              </Tabs>
            </Box>
            <Paper>
              {isLoading || isCreating || !formik ? (
                <LoadingPaperSkeleton sx={{ height: '70vh' }} />
              ) : (
                <Suspense fallback={<LoadingPaperSkeleton />}>
                  {formik && (
                    <Outlet
                      context={{
                        formik,
                        showSaveAndTestDialog,
                        setShowSaveAndTestDialog,
                      }}
                    />
                  )}
                </Suspense>
              )}
            </Paper>
          </Box>
        </form>
      )}
    </>
  )
}
export default CreateEditCampaignContainer
